import { Track } from '@bariendo/lib/constants';
import { getStoredPaymentStatus } from '@bariendo/lib/payment-status';
import {
  REFUND_REQUEST_REASONS,
  refundableAmount,
  RefundRequestCase,
  validRefundAmount,
} from '@bariendo/lib/refund-request';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { createReference, OperationOutcomeError } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconX } from '@tabler/icons-react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { MessageBoxType } from '@/components/Timeline/types';
import { alert } from '@/services/alert';
import { refundRequestAvailable } from '@/services/billing-menu';
import { RefundHistoryEntry } from '@/services/Timeline/refundCards';
import { reportError } from '@/services/error-reporting';
import { formatDollars } from '@/services/money';
import { BOT_WRITE_SETTLE_MS, isBotTimeout } from '@/services/botCalls';
import { fetchBot } from '@/services/services';
import type {
  CreateTreatmentPaymentEvent,
  CreateTreatmentPaymentResult,
} from '../../../../../bots/src/handlers/create-treatment-payment';
import { BillingComposerCard } from './BillingComposerCard';

type Procedure = 'cancel' | 'keep';

/** The payments backend's limit on a request's note. */
const NOTE_MAX_LENGTH = 2000;

/** Fixed copy: the backend's own text names services and ids, so it goes to error reporting. */
function describeRecordError(error: unknown): string {
  const refused =
    (error instanceof OperationOutcomeError && error.outcome.issue?.some((issue) => issue.code === 'forbidden')) ||
    (error instanceof Error && /permission/i.test(error.message));
  return refused
    ? 'You don’t have permission to request a refund. Ask an admin to check your role.'
    : 'Could not send the request. Try again, or reload the page.';
}

/**
 * + Add ▸ Refund or cancellation request (BAR-3069), inline at the top of the timeline like
 * Coverage activity. Writes a request into Finance's queue in the payments backend (BAR-3033)
 * through `create-treatment-payment`, which restages the patient: the stored payment status
 * moves to Refund requested or Cancelled after approval, the stage to Cancelled (BAR-3083), and
 * the backend reverses Won for a request that cancels.
 *
 * - **Self-pay:** Cancel it or Keep it, a reason, the amount (everything paid by default) and a
 *   note. **Request refund** waits for Finance to approve; Keep it is booked as a customer
 *   service fee and leaves the status alone.
 * - **Employer, insurance:** a reason and a note. **Record** is final: a cancellation with no
 *   money back, which Finance's queue does not list.
 */
export function RefundRequestMessageBox(): ReactElement | null {
  const { patientContext, messageBoxContext, paymentOrderContext, refundRequestsContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { setShowMessageInput } = messageBoxContext;
  // The message box and the payment order are the page's, not the patient's: a request that
  // settles after a patient switch must not close or refresh anything on the next patient.
  const currentPatientId = useRef(patient?.id);
  currentPatientId.current = patient?.id;
  // Read live when the write settles: another composer opened meanwhile must stay open, and a
  // box that was swapped out (its refs frozen) does nothing at all.
  const variant = useRef(messageBoxContext.messageBoxVariant);
  variant.current = messageBoxContext.messageBoxVariant;
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const stillHere = (patientId: string | undefined): boolean =>
    mounted.current && currentPatientId.current === patientId && variant.current === MessageBoxType.REFUND_REQUEST;

  if (!patient) {
    return null;
  }
  const formPatientId = patient.id;

  return (
    <RefundRequestBox
      key={patient.id}
      patient={patient}
      amountPaid={paymentOrderContext?.order?.amountPaid ?? 0}
      orderLoading={paymentOrderContext?.loading ?? false}
      requests={refundRequestsContext?.requests}
      onClose={() => {
        if (stillHere(formPatientId)) setShowMessageInput(false);
      }}
      onRecorded={async () => {
        // Always re-read, even with another composer open now: the request exists, and stale
        // requests would offer the row again and let the same refund be filed twice. Each refresh
        // already drops a result for a patient no longer on screen.
        await Promise.all([
          patientContext?.refreshPatient(),
          paymentOrderContext?.refresh(),
          refundRequestsContext?.refresh(),
        ]);
        return mounted.current && currentPatientId.current === formPatientId;
      }}
    />
  );
}

export interface RefundRequestBoxProps {
  patient: Patient;
  /** What the open order has taken, net of refunds. Dollars. */
  amountPaid: number;
  /** The order is still being read: its paid amount is not this patient's yet. */
  orderLoading?: boolean;
  /** The patient's refund requests; undefined until read, or when the read failed. */
  requests: RefundHistoryEntry[] | undefined;
  onClose: () => void;
  /** Refreshes the page after the write; false when the patient on screen is no longer this one.
   *  Closing stays with `onClose`, which leaves another composer opened meanwhile alone. */
  onRecorded: () => Promise<boolean>;
}

export function RefundRequestBox({
  patient,
  amountPaid,
  orderLoading = false,
  requests,
  onClose,
  onRecorded,
}: RefundRequestBoxProps): ReactElement {
  const medplum = useMedplum();
  const { track, loading: trackLoading } = usePatientTrack(patient.id);
  // The last resolved track, so a re-read (a switch announced elsewhere) doesn't flip the fields
  // and clear what the operator picked. Undefined until the first one.
  const [resolvedTrack, setResolvedTrack] = useState<{ track: Track | undefined } | undefined>();
  useEffect(() => {
    if (!trackLoading) setResolvedTrack({ track });
  }, [track, trackLoading]);
  const selfPay = !!resolvedTrack && (resolvedTrack.track ?? Track.D2C) === Track.D2C;

  const [procedure, setProcedure] = useState<Procedure>('cancel');
  const [reason, setReason] = useState<string | null>(null);
  // Follows what was paid until the operator types an amount of their own.
  const [amountEdit, setAmountEdit] = useState<number | string | undefined>();
  // What open requests already ask for is not on offer again (BAR-3069).
  const refundable = refundableAmount(amountPaid, requests ?? []);
  const amount = amountEdit ?? refundable;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const reasonCase: RefundRequestCase = selfPay ? procedure : 'payor';
  // A reason picked from another case's list never travels with this one.
  useEffect(() => {
    setReason(null);
  }, [reasonCase]);

  const refundAmount = selfPay ? validRefundAmount(amount, refundable) : 0;
  // The same rule as + Add's row, for the patient on screen: the box outlives a patient switch.
  const available = refundRequestAvailable(resolvedTrack?.track, getStoredPaymentStatus(patient)?.status, requests);
  // A self-pay refund comes out of what the open order took: with nothing read, say so rather
  // than offer an amount that can never be valid.
  const noOrderPayment = selfPay && amountPaid <= 0;
  const ready = !!resolvedTrack && !orderLoading && requests !== undefined;
  const canRecord = ready && available && !noOrderPayment && !busy && !!reason && refundAmount !== undefined;

  async function record(): Promise<void> {
    if (!canRecord || !reason || refundAmount === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      const input: CreateTreatmentPaymentEvent = {
        operation: 'createRefundRequest',
        patient: createReference(patient),
        refundRequest: {
          patient: createReference(patient),
          // Employer and insurance: always a cancellation, with nothing to refund.
          cancel: selfPay ? procedure === 'cancel' : true,
          reason,
          note: note.trim() || null,
          amount: refundAmount,
        },
      };
      const result = await fetchBot<CreateTreatmentPaymentResult>(medplum, 'create-treatment-payment', input);
      // Recorded, but the procedure is still booked: say so rather than let it read as done (BAR-3051),
      // even when the screen has moved on, since nothing else will.
      if ('procedureCancel' in result && result.procedureCancel === 'failed') {
        alert.error({
          title: 'Couldn’t cancel the procedure',
          message: 'It is still booked. Cancel it from the scheduling page.',
        });
      }
      // Nothing more on a screen that has moved on to another patient.
      if (!(await onRecorded())) return;
      onClose();
      alert.success({
        title: selfPay ? 'Refund requested' : 'Cancellation recorded',
        message: selfPay ? 'Finance approves it in Refunds.' : '',
      });
    } catch (e) {
      reportError(e, { component: 'RefundRequestBox' });
      if (isBotTimeout(e)) {
        // It can still have landed (BAR-3051), even after the bot was stopped: re-read now and
        // again once it has had time to, with Record held disabled, so a recorded request shows
        // and blocks a duplicate. A typed amount would stay valid against what's left; drop it.
        setAmountEdit(undefined);
        setError('The request timed out. It may have gone through: check the timeline before sending it again.');
        await onRecorded();
        await new Promise((resolve) => setTimeout(resolve, BOT_WRITE_SETTLE_MS));
        await onRecorded();
      } else {
        setError(describeRecordError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <BillingComposerCard
      icon={<IconX size={16} />}
      title="Refund or cancellation request"
      onClose={onClose}
      closeDisabled={busy}
      data-testid="refund-request-composer"
    >
      <Stack p="md" gap="md">
        {ready && !available && (
          <Alert color="yellow" role="alert" data-testid="refund-request-unavailable">
            There is nothing to refund or cancel for this patient: no payment or approval, or it is already cancelled.
          </Alert>
        )}
        {ready && available && noOrderPayment && (
          <Alert color="yellow" role="alert" data-testid="refund-request-no-order">
            Couldn’t find a payment on this patient’s open order to refund. Reload, or check the order in Intake and
            billing.
          </Alert>
        )}
        {error && (
          <Alert color="red" role="alert" data-testid="refund-request-error">
            {error}
          </Alert>
        )}

        {selfPay && (
          <Stack gap={4}>
            <Text fz="sm" fw={500}>
              Procedure
            </Text>
            <SegmentedControl
              data-testid="refund-request-procedure"
              value={procedure}
              onChange={(value) => setProcedure(value as Procedure)}
              data={[
                { value: 'cancel', label: 'Cancel it' },
                { value: 'keep', label: 'Keep it' },
              ]}
              disabled={busy}
            />
          </Stack>
        )}

        <SimpleGrid cols={2} spacing="md" verticalSpacing="sm">
          <Select
            label="Reason"
            placeholder="Select a reason"
            data={[...REFUND_REQUEST_REASONS[reasonCase]]}
            value={reason}
            onChange={setReason}
            disabled={!resolvedTrack || busy}
            data-testid="refund-request-reason"
          />
          {selfPay && (
            <NumberInput
              label="Refund amount"
              prefix="$"
              thousandSeparator=","
              decimalScale={2}
              min={0}
              max={refundable}
              value={amount}
              onChange={setAmountEdit}
              description={
                refundable < amountPaid
                  ? `Paid so far: ${formatDollars(amountPaid)} · ${formatDollars(amountPaid - refundable)} already requested`
                  : `Paid so far: ${formatDollars(amountPaid)}`
              }
              error={
                amount !== '' && refundAmount === undefined
                  ? 'More than zero, and no more than is left to refund'
                  : undefined
              }
              disabled={busy}
              data-testid="refund-request-amount"
            />
          )}
        </SimpleGrid>

        <Textarea
          label="Note"
          placeholder="Optional"
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          autosize
          minRows={2}
          maxLength={NOTE_MAX_LENGTH}
          disabled={busy}
        />

        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => void record()}
            loading={busy}
            disabled={!canRecord}
            data-testid="refund-request-record"
          >
            {selfPay ? 'Request refund' : 'Record'}
          </Button>
        </Group>
      </Stack>
    </BillingComposerCard>
  );
}
